import React, { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import api, { formatApiError } from "@/lib/api";
import { Mail, ArrowLeft, ArrowRight, CheckCircle2, Loader2 } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("idle"); // idle | loading | success | error
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    setStatus("loading");
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setStatus("success");
    } catch (ex) {
      setErr(formatApiError(ex) || "Something went wrong — please try again.");
      setStatus("error");
    }
  };

  return (
    <div className="min-h-screen bg-[#FFFDF7]">
      <Navbar />
      <section className="max-w-md mx-auto px-6 lg:px-8 py-14" data-testid="forgot-password-page">
        <Link
          to="/auth?mode=login"
          className="inline-flex items-center gap-1 text-sm font-semibold text-slate-600 hover:text-brandPink"
          data-testid="forgot-back-signin"
        >
          <ArrowLeft className="w-4 h-4" /> Back to sign in
        </Link>

        <div className="mt-6">
          <div className="pill bg-brandPink/15 text-brandPink">
            <Mail className="w-3.5 h-3.5 mr-1.5" /> Password reset
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-semibold text-brandSlate mt-3" data-testid="forgot-title">
            Forgot your password?
          </h1>
          <p className="text-slate-600 mt-3 leading-relaxed">
            Pop your email in below. If we recognise it, we&rsquo;ll send you a secure link to reset your password.
          </p>
        </div>

        {status === "success" ? (
          <div className="card-base p-6 mt-8 bg-brandTeal/10 border-brandTeal/30" data-testid="forgot-success">
            <div className="flex items-start gap-3">
              <CheckCircle2 className="w-6 h-6 text-brandTeal flex-shrink-0" />
              <div>
                <div className="font-display text-lg font-semibold text-brandSlate">Check your inbox</div>
                <p className="text-sm text-slate-600 mt-1 leading-relaxed">
                  If <strong className="break-all">{email}</strong> matches an account, we&rsquo;ve sent you a reset link.
                  The link expires in 30 minutes — didn&rsquo;t get it? Check spam, then try again.
                </p>
                <Link to="/auth?mode=login" className="btn-primary mt-4" data-testid="forgot-goto-signin">
                  Back to sign in <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            </div>
          </div>
        ) : (
          <form onSubmit={submit} className="card-base p-6 md:p-7 mt-8" data-testid="forgot-form">
            <label className="block text-sm font-bold text-brandSlate mb-1">Account email</label>
            <input
              type="email"
              required
              autoFocus
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@email.com"
              className="input-base"
              data-testid="forgot-email-input"
              disabled={status === "loading"}
            />
            {err && (
              <div className="mt-3 text-sm text-rose-700 bg-rose-50 border border-rose-200 rounded-xl p-3" data-testid="forgot-error">
                {err}
              </div>
            )}
            <button
              type="submit"
              disabled={status === "loading"}
              className="btn-primary w-full mt-5 disabled:opacity-60"
              data-testid="forgot-submit-btn"
            >
              {status === "loading" ? (
                <><Loader2 className="w-4 h-4 animate-spin" /> Sending…</>
              ) : (
                <>Send reset link <ArrowRight className="w-4 h-4" /></>
              )}
            </button>
            <div className="text-xs text-slate-500 mt-3 text-center">
              We&rsquo;ll never share your email. See our <Link to="/info/privacy" className="text-brandTeal hover:underline">Privacy Policy</Link>.
            </div>
          </form>
        )}
      </section>
      <Footer />
    </div>
  );
}
